import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColorName } from '@connect-x-app/shared-ui';
import { DateTime, getNow, isLanded, parseDate } from '@connect-x-app/shared-utils';

import { useAppTheme } from './useAppTheme';

type CountdownProps = {
  ata?: DateTime | null;
  atd?: DateTime | null;
  eta?: DateTime | null;
  etd?: DateTime | null;
  sta: DateTime;
  std: DateTime;
};

type CountdownState = {
  countdownLabel: string;
  countdownString: string;
  countdownTextColor: ColorName;
  isLanded: boolean;
};

// Which of the three departure timestamps the countdown is measured against: an actual departure
// wins over an estimated one, which wins over the scheduled fallback.
const getCountdownLabelKey = (hasActualDeparture: boolean, hasEstimatedDeparture: boolean) => {
  if (hasActualDeparture) {
    return 'COUNTDOWN.OUT';
  }
  if (hasEstimatedDeparture) {
    return 'COUNTDOWN.ETD';
  }
  return 'COUNTDOWN.STD';
};

const formatTimeString = (totalSeconds: number) => {
  const days = Math.floor(totalSeconds / (3600 * 24));
  const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const dayPrefix = days > 0 ? `${days}d ` : '';

  return `${dayPrefix}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

export const useCountdown = ({ ata, atd, eta, etd, sta, std }: CountdownProps): CountdownState => {
  const { t } = useTranslation();
  const { colors } = useAppTheme();
  const intervalRef = useRef<NodeJS.Timeout | number>(null);
  const previousCountdownState = useRef<CountdownState>(null);
  const [currentCountdownState, setCurrentCountdownState] = useState<CountdownState | null>(null);

  const targetTime = useMemo(() => {
    const departureDate = atd ? std : (etd ?? std);
    return parseDate(departureDate).getTime();
  }, [atd, etd, std]);

  const calculateCountdownState = useCallback(
    (isLanded: boolean): CountdownState => {
      const currentTime = atd ? parseDate(atd).getTime() : getNow().getTime();
      const timeDifference = targetTime - currentTime;
      const isTargetDatePassed = timeDifference < 0;

      const absoluteDifference = Math.abs(timeDifference);
      const totalSeconds = Math.floor(absoluteDifference / 1000);

      return {
        countdownLabel: t(getCountdownLabelKey(!!atd, !!etd)),
        countdownString: `${isTargetDatePassed ? '+ ' : '- '}${formatTimeString(totalSeconds)}`,
        countdownTextColor: isTargetDatePassed ? colors.caution : colors.success,
        isLanded: isLanded,
      };
    },
    [atd, targetTime, t, etd, colors.caution, colors.success],
  );

  useEffect(() => {
    const tick = () => {
      const isRoomLanded = isLanded(sta, eta, ata);

      if (atd) {
        setCurrentCountdownState(prevState => {
          if (prevState?.countdownLabel !== t('COUNTDOWN.OUT')) {
            return calculateCountdownState(isRoomLanded);
          }
          return { ...prevState, isLanded: isRoomLanded };
        });
      } else {
        const newState = calculateCountdownState(isRoomLanded);
        // Update state only if it has changed
        if (!previousCountdownState.current || JSON.stringify(newState) !== JSON.stringify(previousCountdownState.current)) {
          setCurrentCountdownState(newState);
          previousCountdownState.current = newState;
        }
      }

      if (!isRoomLanded) {
        // Schedule the next tick
        const now = Date.now();
        const msUntilNextSecond = 1000 - (now % 1000);
        intervalRef.current = setTimeout(tick, msUntilNextSecond);
      }
    };

    tick(); // Initial tick

    return () => {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
      }
    };
  }, [calculateCountdownState, atd, sta, eta, ata, t]);

  return currentCountdownState ?? calculateCountdownState(isLanded(sta, eta, ata));
};
